import { MetricsDispatcher } from 'lib/MetricsDispatcher';
import userAgent from 'lib/currentUserAgent';
import Config from 'Config';
import {
  eventNames as adminLookerEventNames,
  metricNames as adminLookerMetricNames,
} from 'constants/AdminOrgChartConstants';

export type MetricName =
  | 'app_crashed'
  | 'app_launched'
  | 'app_opened'
  // Composer Search.
  | 'composer.search.view.ctr.success'
  | 'composer.search.view.ctr.rejection'
  | 'composer.search.view.ctr.success.character_count'
  | 'composer.search.view.ctr.rejection.character_count'
  // Composer Composition.
  | 'composer.composition.view.sent.success'
  | 'composer.composition.view.sent.failed'
  // Stream Composer.
  | 'stream.chat.view.sent.success'
  | 'stream.chat.view.sent.failed'
  | 'stream.chat.view.gql.sent.attempt'
  | 'stream.chat.view.gql.sent.success'
  | 'stream.chat.view.gql.sent.failed'
  // u13 conversatioins
  | 'conversations.send.success'
  | 'conversations.send.error'
  // Apollo cache organizations queries (see)
  | 'apollo_cache.organizations.read'
  | 'apollo_cache.organizations.read.cached.all'
  | 'apollo_cache.organizations.read.cached.some'
  | 'apollo_cache.organizations.read.cached.none'
  // Admin stats
  | typeof adminLookerEventNames[keyof typeof adminLookerEventNames];

export type MetricData = {
  type:
    | 'increment_count'
    | 'decrement_count'
    | 'timing_milliseconds'
    | 'gauge'
    | 'histogram'
    | 'unique_set';
  name: MetricName;
  // Ignored for incr/decr.
  value: number;
  // Only used for incr/decr if needing value > 1.
  countValue?: number;
  // Only used for unique set.
  setValue?: string;
  sampleRate?: number;
  tags: { [key: string]: string };
};

export const MetricTagKeys = [
  // Composer Search.
  'did_type_search_query',
  'is_urgent',
  // Composer Composition.
  'has_attachment',
  'is_scheduled',
  'has_plt_translations',
  'message_submission_type',
  // Admin stats
  'dashboard_name',
  'dashboard_tile',
] as const;

export type MetricTagKey = typeof MetricTagKeys[number];

export type MetricTagValue =
  | 'true'
  | 'false'
  // Composer Composition.
  | 'new_scheduled_message'
  | 'update_scheduled_message'
  | 'group_chat'
  | 'new_message'
  // Admin stats ()
  | typeof adminLookerMetricNames[keyof typeof adminLookerMetricNames];

export const boolTagValue = (b: boolean): MetricTagValue => (b ? 'true' : 'false');

type OptionalParams = {
  tags?: { [key in MetricTagKey]?: MetricTagValue };
  sampleRate?: number;
};

export class MetricTracking {
  dispatcher: MetricsDispatcher;

  constructor() {
    this.dispatcher = new MetricsDispatcher();
    this.dispatcher.addSessionProperties({
      client_segment: userAgent.clientSegment(),
      client_version: Config.Environment.EMPIRE_RELEASE,
      os_name: userAgent.osDescription(),
    });
  }

  count(name: MetricName, value: number, optionalParams?: OptionalParams) {
    this.dispatcher.enqueue({
      type: 'increment_count',
      name,
      value: 0,
      countValue: value,
      sampleRate: optionalParams?.sampleRate,
      tags: (optionalParams?.tags as { [key: string]: string }) || {},
    });
  }

  increment(name: MetricName, optionalParams?: OptionalParams) {
    this.dispatcher.enqueue({
      type: 'increment_count',
      name,
      value: 0,
      sampleRate: optionalParams?.sampleRate,
      tags: (optionalParams?.tags as { [key: string]: string }) || {},
    });
  }

  decrement(name: MetricName, optionalParams?: OptionalParams) {
    this.dispatcher.enqueue({
      type: 'decrement_count',
      name,
      value: 0,
      sampleRate: optionalParams?.sampleRate,
      tags: (optionalParams?.tags as { [key: string]: string }) || {},
    });
  }

  timingMilliseconds(name: MetricName, value: number, optionalParams?: OptionalParams) {
    this.dispatcher.enqueue({
      type: 'timing_milliseconds',
      name,
      value,
      sampleRate: optionalParams?.sampleRate,
      tags: (optionalParams?.tags as { [key: string]: string }) || {},
    });
  }

  gauge(name: MetricName, value: number, optionalParams?: OptionalParams) {
    this.dispatcher.enqueue({
      type: 'gauge',
      name,
      value,
      sampleRate: optionalParams?.sampleRate,
      tags: (optionalParams?.tags as { [key: string]: string }) || {},
    });
  }

  histogram(name: MetricName, value: number, optionalParams?: OptionalParams) {
    this.dispatcher.enqueue({
      type: 'histogram',
      name,
      value,
      sampleRate: optionalParams?.sampleRate,
      tags: (optionalParams?.tags as { [key: string]: string }) || {},
    });
  }

  uniqueSet(name: MetricName, setValue: string, optionalParams?: OptionalParams) {
    this.dispatcher.enqueue({
      type: 'unique_set',
      name,
      value: 0,
      setValue,
      sampleRate: optionalParams?.sampleRate,
      tags: (optionalParams?.tags as { [key: string]: string }) || {},
    });
  }
}

export default new MetricTracking();
